import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { environment } from "environment";
import { getToken, ListResponse, Order, UUID } from "helpers";

// Comment interfaces
export interface Comment {
  id: number;
  uuid: string;
  comment: string;
  created_at: string;
  updated_at: string;
}

export interface ClientComment {
  client: {
    id: number;
    client_id: number;
    uuid: string;
  };
  comments: Comment[];
}

export interface EndUserUpdateData {
  name: string;
  first_name: string;
  family_name: string;
  phone_number: string;
  city_id: number;
  language: string;
  enabled: boolean;
  credits: string;
}

export interface EndUserDevice {
  id: number;
  device_key: string;
  remembered: boolean;
  last_used: string;
  is_active: boolean;
  open_session_at: string;
  closed_session_at: string;
  signup_source: string;
  created_at: string;
  updated_at: string;
}

export interface EndUser {
  id: number;
  uuid: string;
  name: string;
  first_name: string;
  family_name: string;
  email: string;
  phone_number: string;
  credits: string;
  type: {
    id: number;
    name: string;
  };
  city: {
    id: number;
    name: string;
    country: string;
  };
  language: string;
  picture: {
    name: string;
    url: string;
  };
  client_state: "GUEST_MODE" | "REGISTERED_MODE";
  enabled: boolean;
  confirmation_status: boolean;
  ip_address: string;
  last_login: string;
  logged_in: boolean;
  mfa: boolean;
  devices: EndUserDevice[];
  created_at: string;
  updated_at: string;
}

export interface EndUsersResponse extends ListResponse {
  clients: EndUser[];
}

// Define query options
export interface ListEndUserOptions {
  page?: number;
  pageSize?: number;
  search?: string;
  sort?: "full_name" | "city" | "email" | "phone_number" | "last_sign_in";
  order?: Order;
  confirmation_status?: boolean;
  client_state?: "GUEST_MODE" | "REGISTERED_MODE";
  city_id?: number;
}

// Define the API service
export const endUserApi = createApi({
  reducerPath: "endUserApi",
  baseQuery: fetchBaseQuery({
    baseUrl: `${environment.dev.baseUrl}/mobile/client/manage`,
    prepareHeaders: (headers) => {
      headers.set("authorization-token", getToken() || "");
      return headers;
    },
  }),
  endpoints: (builder) => ({
    getEndUsers: builder.query<EndUsersResponse, ListEndUserOptions>({
      query: ({ page = 0, pageSize = 10, search, sort, order, confirmation_status, client_state, city_id }) => ({
        url: "list",
        params: {
          page: page + 1,
          pageSize,
          search: search ? search.replace(/ /g, "-") : undefined,
          sort,
          order,
          confirmation_status,
          client_state,
          city_id,
        },
      }),
      transformResponse: (response: EndUsersResponse) => ({
        ...response,
        currentPage: response.currentPage - 1,
      }),
    }),

    // Comment endpoints
    getComments: builder.query<ClientComment[], UUID>({
      query: (username) => ({
        url: `${username}/comment/list`,
      }),
      transformErrorResponse: (error: any) => error.data.apierror,
    }),

    createComment: builder.mutation<any, { username: UUID; comment: string }>({
      query: ({ username, comment }) => ({
        url: `${username}/comment/create`,
        method: "PUT",
        body: {
          comment,
        },
      }),
      transformErrorResponse: (error: any) => error.data.apierror,
    }),

    updateEndUser: builder.mutation<any, { username: UUID; userData: EndUserUpdateData }>({
      query: ({ username, userData }) => ({
        url: username,
        method: "POST",
        body: userData,
      }),
      transformErrorResponse: (error: any) => error.data.apierror,
    }),
  }),
});

export const {
  useGetEndUsersQuery,
  useLazyGetEndUsersQuery,
  useGetCommentsQuery,
  useLazyGetCommentsQuery,
  useCreateCommentMutation,
  useUpdateEndUserMutation,
  usePrefetch: useEndUserApiPrefetch,
} = endUserApi;
